import React, { useEffect, useState } from 'react';
import {
  DESCRIPTION_LABEL,
  DESCRIPTION_PLACEHOLDER,
  GUTTER,
  INTAKES,
  LABEL,
  NOTES_LABEL,
  PLACEHOLDER,
  PRODUCT_DETAILS,
  PRODUCT_INFORMATION,
  PRODUCT_NAME_REQUIRED,
  REVENUE_TYPE,
  TEXT,
} from '@moxie/constants';
import {
  Checkbox,
  Col,
  Collapse,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Skeleton,
  Spin,
} from 'antd';
import {
  IGetPopupContainer,
  IInstituteInterface,
  ProductFormProps,
} from '@shared-components/models';
import { CaretRightOutlined } from '@ant-design/icons';
import {
  getInstitutionBranchesApi,
  getInstitutionBranchesList,
  getInstitutionByService,
  getInstitutionDetail,
  getServices,
  getSubjectDisciplines,
  getWorkflowsByInstitution,
  productServices,
} from '@admin/services.api';
import { Editor } from 'libs/shared/src/modules/forms/editor';
import { formExtraNotes, preventMinus } from 'libs/shared/src/functions';
import { useAppSelector } from '@admin/core';
const { Option } = Select;
const { Panel } = Collapse;

const ProductForm = ({
  form,
  initialData,
  onSubmit,
  setDisabled,
  isEdit,
}: ProductFormProps) => {
  const [subjectDisciplineData, setSubjectDisciplineData] = useState<any>([]);
  const [subjectAreasData, setSubjectAreasData] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [serviceId, setServiceId] = useState<String>('');

  const [subTypeLevelData, setSubTypeLevelData] = useState([]);
  const [workflowsData, setWorkflowsData] = useState([]);
  const [institutionData, setInstitutionData] = useState<IInstituteInterface>(
    []
  );
  const [institutionBranchesData, setInstitutionBranchesData] = useState([]);
  const [productTypeData, setProductTypeData] = useState<any>([]);
  const [formDataLoading, setFormDataLoading] = useState<boolean>(false);
  const productLoading = useAppSelector((state) => state.product.loading);
  const [serviceLoading, setServiceLoading] = useState<boolean>(false);
  const [institutionLoading, setInstitutionLoading] = useState<boolean>(false);
  const [branchesLoading, setBranchesLoading] = useState<boolean>(false);
  const [workflowLoading, setWorkflowLoading] = useState<boolean>(false);
  const [productTypeLoading, setProductTypeLoading] = useState<boolean>(false);
  const [degreeLevelLoading, setDegreeLevelLoading] = useState<boolean>(false);
  const [disciplineLoading, setDisciplineLoading] = useState<boolean>(false);
  const [subjectAreaLoading, setSubjectAreaLoading] = useState<boolean>(false);

  const getSubjectDiscipline = async () => {
    setDisciplineLoading(true);
    const res = await getSubjectDisciplines();
    setSubjectDisciplineData(res?.data?.data);
    setDisciplineLoading(false);
    return res?.data?.data;
  };

  const getService = async () => {
    setServiceLoading(true);
    const res = await getServices();
    setServiceData(res?.data?.data);
    setServiceLoading(false);
  };

  const getProductTypes = async (serviceId: string) => {
    setProductTypeLoading(true);
    const res = await productServices.apiGetProductTypes({ serviceId });
    setProductTypeData(res?.data?.data);
    setProductTypeLoading(false);
  };

  const getInstitution = async (id: string) => {
    setInstitutionLoading(true);
    const res = await getInstitutionByService(id);
    setInstitutionData(res?.data?.data);
    setInstitutionLoading(false);
    await getProductTypes(id);
    !isEdit &&
      form.setFieldsValue({
        branches: undefined,
        workflows: undefined,
        institution_id: undefined,
        product_type_id: undefined,
      });
  };

  const getSubjectAreas = (data: any, subjectDisciplineId: string) => {
    setSubjectAreaLoading(true);
    setSubjectAreasData(
      data.find((item: any) => item.id === subjectDisciplineId)
        ?.subject_areas || []
    );
    setSubjectAreaLoading(false);
  };

  const handleDisciplineChange = (disciplineId: string) => {
    form.setFieldsValue({
      subject_disciplines_id: disciplineId,
      subject_area_id: undefined,
    });
    getSubjectAreas(subjectDisciplineData, disciplineId);
  }

  const onFieldsChange = (allFields: string | any[]) => {
    if (allFields.length === 1) {
      setDisabled(false);
    }
  };

  const getInstitutionBranches = async (institutionId: string) => {
    setBranchesLoading(true);
    const institutionBranchRes = await getInstitutionBranchesList(institutionId)
    setInstitutionBranchesData(institutionBranchRes?.data?.data);
    setBranchesLoading(false);
    setWorkflowLoading(true);
    const res = await getWorkflowsByInstitution(institutionId);
    setWorkflowsData(res?.data?.data);
    setWorkflowLoading(false);
    form.setFieldsValue({
      branches: undefined,
      workflows: undefined,
    });
  };

  const handleProductType = async (value: any) => {
    form.resetFields(['product_sub_type_id']);
    setDegreeLevelLoading(true);
    setSubTypeLevelData(
      productTypeData
        ?.map((item: any) => {
          if (item.id === value) {
            return item.sub_type_level;
          }
        })
        .filter((item: any) => Boolean(item))[0]
    );
    setDegreeLevelLoading(false);
  };

  const setFieldsValue = async () => {
    const res = await getInstitutionByService(
      initialData?.institution?.service_id
    );
    setInstitutionData(res?.data?.data);
    await getInstitutionBranches(initialData?.institution?.id);
    const branchesList = initialData?.institutionBranches?.map(
      (item: any) => item?.institution_branch_id
    );
    const workflowList = initialData?.institution_workflows?.map(
      (item: any) => item?.id
    );
    setSubTypeLevelData(initialData?.productType?.sub_type_level);
    form.setFieldsValue({
      product_duration_month: initialData.product_duration_month,
      product_duration_week: initialData.product_duration_week,
      product_duration_year: initialData.product_duration_year,
      branches: branchesList,
      workflows: workflowList,
      service: initialData?.institution?.service_id,
      institution_id: !institutionLoading ? initialData?.institution?.id : '',
      name: initialData?.name,
      product_sub_type_id: initialData.product_sub_type_id,
      product_type_id: initialData.product_type_id,
      program_link: initialData?.program_link,
      revenue_type: initialData?.revenue_type,
      description: initialData.description,
      subject_disciplines_id: initialData?.subject_disciplines_id,
      subject_area_id: initialData?.subject_area_id,
      identification_system_code: initialData?.identification_system_code,
      intakes: initialData?.intakes
    });

  };

  const handler = async (subjectDisciplinesData: any, subjectDisciplineId: string) => {
    await setFieldsValue();
    getSubjectAreas(subjectDisciplinesData, subjectDisciplineId);
    setFormDataLoading(false);
  };

  useEffect(() => {
    getService().then(async () => {
      const subjectDisciplines = await getSubjectDiscipline();
      if (isEdit) setFormDataLoading(true);
      if (isEdit && initialData?.id) {
        getInstitution(initialData?.institution?.service_id).then(() => {
          handler(subjectDisciplines, initialData?.subject_disciplines_id);
        });
      } else {
        form.setFieldsValue({
          product_duration_month: 0,
          product_duration_week: 0,
          product_duration_year: 0,
        });
      }

    })
  }, [initialData?.id, form]);

  return formDataLoading ? (
    <Skeleton active />
  ) : (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      scrollToFirstError
      onFieldsChange={onFieldsChange}
    >
      <Collapse
        defaultActiveKey={['1']}
        bordered={false}
        ghost={true}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Panel header={<span>{PRODUCT_DETAILS}</span>} key="1">
          <Row gutter={GUTTER}>
            <Col span={12}>
              <Form.Item
                label={LABEL.NAME}
                name="name"
                rules={[
                  { required: true, max: 250 },
                  {
                    whitespace: true,
                    type: 'string',
                    message: PRODUCT_NAME_REQUIRED,
                  },
                ]}
              >
                <Input className="note-title-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div id="product_service" className="relative">
                <Form.Item
                  label={LABEL.SERVICE}
                  name="service"
                  rules={[{ required: true }]}
                >
                  <Select
                    value={initialData?.service}
                    disabled={isEdit}
                    onChange={getInstitution}
                    placeholder={PLACEHOLDER.SELECT_SERVICE}
                    showSearch
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_service')
                    }
                    optionFilterProp="label"
                  >
                    {serviceLoading ? (
                      <Option disabled value={''}>
                        <Spin spinning size="small" className="full-width" />
                      </Option>
                    ) : (
                      serviceData?.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.id} label={data?.name}>
                            {data?.name}
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <div id="product_institution" className="relative">
                <Form.Item
                  label={LABEL.INSTITUTION}
                  name="institution_id"
                  rules={[{ required: true }]}
                >
                  <Select
                    onChange={getInstitutionBranches}
                    disabled={isEdit}
                    showSearch
                    value={initialData?.institution?.id}
                    placeholder={PLACEHOLDER.SELECT_INSTITUTION}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_institution')
                    }
                    optionFilterProp="label"
                  >
                    {institutionLoading ? (
                      <Option disabled value={''}>
                        <Spin spinning size="small" className="full-width" />
                      </Option>
                    ) : (
                      institutionData?.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.id} label={data?.name}>
                            {data?.name}
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>

            <Col span={12}>
              <div id="product_branches" className="relative">
                <Form.Item
                  label={LABEL.BRANCHES}
                  name="branches"
                  rules={[{ required: true }]}
                >
                  <Select
                    className="note-title-input"
                    placeholder={PLACEHOLDER.SELECT_BRANCH}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_branches')
                    }
                    mode="multiple"
                    showSearch
                    optionFilterProp='label'
                  >
                    {workflowLoading ? (
                      <Option value={''} disabled>
                        <Spin spinning size="small" className="full-width" />
                      </Option>
                    ) : (
                      institutionBranchesData?.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.name} label={data?.name}>
                            {data?.name}
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <div id="product_workflow" className="relative">
                <Form.Item
                  label={LABEL.WORKFLOW}
                  name="workflows"
                  rules={[{ required: true }]}
                >
                  <Select
                    className="note-title-input"
                    placeholder={PLACEHOLDER.SELECT_WORKFLOW}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_workflow')
                    }
                    mode="multiple"
                    showSearch
                    optionFilterProp='label'
                  >
                    {workflowLoading ? (
                      <Option value={''} disabled>
                        <Spin spinning size="small" className="full-width" />
                      </Option>
                    ) : (
                      workflowsData &&
                      workflowsData.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.id} label={`${data?.workflow?.workflowType?.name} ${data?.workflow?.country}`}>
                            {data?.workflow?.workflowType?.name} (
                            {data?.workflow?.country})
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <Form.Item
                name="identification_system_code"
                rules={[{ max: 15 }]}
                label={LABEL.CRICOS_COURSE_CODE}
              >
                <Input className="note-title-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div id="product_type" className="relative">
                <Form.Item
                  label={LABEL.PRODUCT_TYPE}
                  name="product_type_id"
                  rules={[{ required: true }]}
                >
                  <Select
                    className="note-title-input"
                    value={initialData?.product_type_id}
                    onChange={handleProductType}
                    placeholder={PLACEHOLDER.PRODUCT_TYPE}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_type')
                    }
                    showSearch
                    optionFilterProp='label'
                  >
                    {productTypeLoading ? (
                      <Option value={''} disabled>
                        <Spin spinning className="full-width" size="small" />
                      </Option>
                    ) : (
                      productTypeData?.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.id} label={data?.name}>
                            {data?.name}
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <div id="product_sub_type_level" className="relative">
                <Form.Item
                  label={LABEL.PRODUCT_SUB_TYPE}
                  name="product_sub_type_id"
                  rules={[{ required: true }]}
                >
                  <Select
                    className="note-title-input"
                    value={initialData?.product_sub_type_id}
                    placeholder={PLACEHOLDER.PRODUCT_SUB_TYPE}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_sub_type_level')
                    }
                    showSearch
                    optionFilterProp='label'
                  >
                    {degreeLevelLoading ? (
                      <Option disabled value={''}>
                        <Spin spinning className="full-width" size="small" />
                      </Option>
                    ) : (
                      subTypeLevelData?.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.id} label={data?.name}>
                            {data?.name}
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>

            <Col span={24}>
              <Form.Item
                label={LABEL.COMMISSION_TYPE}
                name="revenue_type"
                rules={[{ required: true }]}
              >
                <Radio.Group className="note-title-input">
                  <Radio value="Commission" key="1" checked={true}>
                    {REVENUE_TYPE.COMMISSION_FROM_PARTNER}
                  </Radio>
                  <Radio key="2" value="Contact fee">
                    {REVENUE_TYPE.REVENUE_FROM_CONTACT}
                  </Radio>
                  ))
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>
      <Collapse
        defaultActiveKey={['1']}
        bordered={false}
        ghost={true}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Panel header={<span>{PRODUCT_INFORMATION}</span>} key="1">
          <Row gutter={GUTTER}>
            <Col span={12}>
              <div id="product_discipline" className="relative">
                <Form.Item label={LABEL.SUBJECT} name="subject_disciplines_id">
                  <Select
                    onChange={handleDisciplineChange}
                    className="note-title-input"
                    value={initialData?.subject_disciplines_id}
                    placeholder={LABEL.SELECT_DISCIPLINE}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_discipline')
                    }
                    showSearch
                    optionFilterProp='label'
                  >
                    {disciplineLoading ? (
                      <Option value={''} disabled>
                        <Spin spinning className="full-width" size="small" />
                      </Option>
                    ) : (
                      subjectDisciplineData?.map((data: any) => {
                        return (
                          <Option value={data?.id} key={data?.id} label={data?.discipline}>
                            {data?.discipline}
                          </Option>
                        );
                      })
                    )}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <div id="product_subject_area" className="relative">
                <Form.Item
                  label={LABEL.SUBJECT_AREA}
                  name="subject_area_id"
                  extra={
                    !form.getFieldValue('subject_disciplines_id')
                      ? formExtraNotes('Discipline')
                      : ''
                  }
                >
                  <Select
                    className="note-title-input"
                    value={initialData?.subject_area_id}
                    placeholder={LABEL.SELECT_SUBJECT_AREA}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_subject_area')
                    }
                    showSearch
                    optionFilterProp='label'
                    options={subjectAreasData.map((payload: any) => ({
                      value: payload.id,
                      label: payload.name
                    }))}
                    loading={subjectAreaLoading}
                  >
                  </Select>
                </Form.Item>
              </div>
            </Col>

            <Col span={16}>
              <p className="ant-form-text padding-bottom-1">{TEXT.DURATIONS}</p>
              <br></br>
              <Row className="product-addon">
                <Col span={8}>
                  <Form.Item name="product_duration_year">
                    <Input
                      min={0}
                      onKeyPress={preventMinus}
                      type="number"
                      addonAfter={TEXT.YEARS}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={'product_duration_month'}>
                    <Input
                      min={0}
                      type="number"
                      onKeyPress={preventMinus}
                      width={30}
                      addonAfter={TEXT.MONTHS}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="product_duration_week">
                    <Input
                      min={0}
                      onKeyPress={preventMinus}
                      type="number"
                      addonAfter={TEXT.WEEKS}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={12}>
              <div id="product_intake" className="relative">
                <Form.Item label={LABEL.INTAKES} name="intakes" validateFirst
                  rules={[{ required: true, message: "Please select intakes" }]}
                >
                  <Select
                    className="note-title-input"
                    placeholder={PLACEHOLDER.INTAKES}
                    getPopupContainer={(): IGetPopupContainer =>
                      document.getElementById('product_intake')
                    }
                    mode={'multiple'}
                  >
                    {INTAKES.map((data) => {
                      return (
                        <Option value={data?.value} key={data?.value}>
                          {data?.name}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col span={24}>
              <Form.Item label={TEXT.PROGRAM_LINK} name="program_link">
                <Input
                  placeholder={TEXT.PROGRAM_LINK}
                  className="note-title-input"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={DESCRIPTION_LABEL} name="description">
                <Editor
                  value="description"
                  placeholder={DESCRIPTION_PLACEHOLDER}
                />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </Form>
  );
};
export { ProductForm };
